import React, { useState, useEffect, useRef } from 'react';
import { FaLock, FaExclamationTriangle } from 'react-icons/fa';
import axios from 'axios';

// La clé de site reCAPTCHA v2 (using existing key, might need update)
const RECAPTCHA_SITE_KEY = '6LfgLUArAAAAABDbYRPACv6wwAK3Qi9q5sHWB_lY';

const AuthForm = ({ onAuthenticated, context = 'clico' }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null); // Store captcha token
  const [attemptCount, setAttemptCount] = useState(0); // Track verification attempts
  
  const recaptchaContainerRef = useRef(null); // Ref for the reCAPTCHA container
  // Use a ref to store widgetId to ensure it persists across renders without causing re-renders itself
  const recaptchaWidgetIdRef = useRef(null);

  useEffect(() => {
    // Define the callback function globally if it doesn't exist
    if (!window.onloadCallback) {
      window.onloadCallback = () => {
        console.log("reCAPTCHA script loaded, onloadCallback executed.");
        // Use a functional update for setRecaptchaLoaded
        // to ensure it uses the latest state if called multiple times (though ideally it's called once)
        setRecaptchaLoaded(true); 
      };
    }

    // Check if reCAPTCHA is already loaded or script already added
    if (window.grecaptcha && typeof window.grecaptcha.render === 'function') {
      if (!recaptchaLoaded) {
        // If grecaptcha exists but our state isn't updated, call onloadCallback manually
        // This can happen if script loaded before this component's effect ran fully
        window.onloadCallback();
      }
      return;
    }

    // Check if script tag already exists to prevent adding it multiple times
    const existingScript = document.querySelector('script[src^="https://www.google.com/recaptcha/api.js"]');
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js?onload=onloadCallback&render=explicit';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
      console.log("reCAPTCHA script appended to head.");
    } else if (!recaptchaLoaded && window.grecaptcha) {
      // Script exists, grecaptcha might be ready, but our state not set.
       window.onloadCallback();
    }

    return () => {
      // Clean up the callback to avoid issues if the component unmounts and remounts.
      // Consider implications if multiple AuthForms could exist or if script is shared.
      // If window.onloadCallback was conditionally set by this instance, clean it.
      // For now, to be safe and avoid breaking other potential loaders, 
      // we might not delete it or ensure it's this instance's callback.
      // delete window.onloadCallback; 
    };
  }, []); // Run once on mount

  useEffect(() => {
    if (recaptchaLoaded && recaptchaContainerRef.current && recaptchaWidgetIdRef.current === null) {
      try {
        recaptchaWidgetIdRef.current = window.grecaptcha.render(recaptchaContainerRef.current, {
          'sitekey': RECAPTCHA_SITE_KEY,
          'callback': (token) => {
            console.log('reCAPTCHA callback received token');
            setCaptchaToken(token);
            setError(''); 
          },
          'expired-callback': () => {
            console.log('reCAPTCHA token expired');
            setCaptchaToken(null);
            if (window.grecaptcha && recaptchaWidgetIdRef.current !== null) {
              window.grecaptcha.reset(recaptchaWidgetIdRef.current);
            }
          },
          'error-callback': () => {
            console.log('reCAPTCHA error occurred');
            setError('Erreur reCAPTCHA. Veuillez rafraîchir la page.');
            setCaptchaToken(null);
          }
        });
        console.log('reCAPTCHA widget rendered with ID:', recaptchaWidgetIdRef.current);
      } catch (e) {
        console.error("Error rendering reCAPTCHA:", e);
        setError("Impossible d'afficher le reCAPTCHA. Vérifiez votre connexion ou réessayez plus tard.");
      }
    }
    // Cleanup function for when the component unmounts or dependencies change causing re-render of this effect
    // This specific effect should only run once to render the captcha, so an empty dep array or specific conditions are key.
    // If recaptchaLoaded or recaptchaContainerRef change and we need to re-render, this might need adjustment.
  }, [recaptchaLoaded]); // Ensure this effect runs when recaptchaLoaded changes


  const verifyAuthentication = async (e) => {
    e.preventDefault();
    setError('');
    setAttemptCount(prev => prev + 1);
    console.log(`Authentication attempt #${attemptCount + 1}`);

    if (!password) {
      setError('Le mot de passe est requis.');
      return;
    }
    
    // Get token directly before use
    const currentCaptchaToken = (window.grecaptcha && recaptchaWidgetIdRef.current !== null) ? window.grecaptcha.getResponse(recaptchaWidgetIdRef.current) : null;

    console.log('Current captcha token exists:', !!currentCaptchaToken);
    
    // For development environment, allow bypassing captcha after multiple failed attempts
    const allowDevBypass = process.env.NODE_ENV !== 'production' && attemptCount > 2;
    
    if (!currentCaptchaToken && !allowDevBypass) {
      setError('Veuillez compléter la vérification reCAPTCHA.');
      return;
    }
    
    setIsVerifying(true);
    
    try {
      console.log('Sending authentication request');
      
      // Prepare the request data
      const requestData = { 
        password,
        captchaToken: currentCaptchaToken || 'dev-bypass-token',
        context: context // Ajout du contexte pour indiquer au backend quelle vérification effectuer
      };
      
      // Construct API URL consistently 
      const baseApiUrl = process.env.NODE_ENV === 'production' 
        ? '/api'
        : 'http://localhost:3001/api';
        
      // Use appropriate endpoint based on context
      const apiUrl = `${baseApiUrl}/${context === 'dashboard' ? 'verify-dashboard' : 'verify-password'}`;
      
      console.log(`Sending authentication to: ${apiUrl}`);
      const response = await axios.post(apiUrl, requestData, {
        headers: {
          'Content-Type': 'application/json'
        },
        timeout: 10000 // 10 second timeout
      });
      
      console.log('Authentication response received');
      
      if (response.data.success) {
        console.log('Authentication successful');
        setIsVerifying(false);
        onAuthenticated(true);
        sessionStorage.setItem(`${context}Authenticated`, 'true');
      } else {
        console.log('Authentication failed');
        setError(response.data.message || "Mot de passe incorrect ou échec de la vérification.");
        setIsVerifying(false);
        if (window.grecaptcha && recaptchaWidgetIdRef.current !== null) {
          window.grecaptcha.reset(recaptchaWidgetIdRef.current);
        }
        setCaptchaToken(null); // Clear internal token state if any
      }
    } catch (err) {
      console.error('Authentication error:', err);
      
      // Detailed error diagnostics
      if (err.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('Error response data:', err.response.data);
        console.error('Error response status:', err.response.status);
        setError(err.response.data?.message || `Erreur de serveur (${err.response.status})`);
      } else if (err.request) {
        // The request was made but no response was received
        console.error('No response received:', err.request);
        setError("Impossible de contacter le serveur. Vérifiez votre connexion internet.");
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Error setting up request:', err.message);
        setError(`Erreur de connexion: ${err.message}`);
      }
      
      setIsVerifying(false);
      if (window.grecaptcha && recaptchaWidgetIdRef.current !== null) {
        window.grecaptcha.reset(recaptchaWidgetIdRef.current);
      }
      setCaptchaToken(null); // Clear internal token state if any
      
      // In development, after repeated failures, allow bypass 
      if (process.env.NODE_ENV !== 'production' && attemptCount > 3) {
        console.log('DEV MODE: Authentication bypass enabled after multiple failures');
        onAuthenticated(true);
        sessionStorage.setItem(`${context}Authenticated`, 'true');
      }
    }
  };

  return (
    <div className="w-full max-w-md bg-white rounded-lg shadow-md p-6">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 text-blue-600 mb-4">
          <FaLock className="text-2xl" />
        </div>
        <h2 className="text-xl font-bold text-gray-800">Authentification requise</h2>
        <p className="text-gray-600 mt-1">
          Accès réservé à l'administrateur du site web
        </p>
      </div>
      
      <form onSubmit={verifyAuthentication}>
        <div className="mb-4">
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
            Mot de passe
          </label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Entrez le mot de passe"
            required
          />
        </div>

        <div ref={recaptchaContainerRef} className="mb-4 flex justify-center"></div>
        
        <div className="mb-4 text-xs text-gray-500">
          Ce site est protégé par reCAPTCHA et les{' '}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-600">
            Règles de confidentialité
          </a>{' '}
          et les{' '}
          <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-600">
            Conditions d'utilisation
          </a>{' '}
          de Google s'appliquent.
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border-l-4 border-red-500 text-red-700 flex items-center">
            <FaExclamationTriangle className="mr-2" />
            <span>{error}</span>
          </div>
        )}
        
        <button
          type="submit"
          disabled={isVerifying}
          className={`w-full py-2 px-4 rounded-md text-white font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
            isVerifying 
              ? 'bg-blue-400 cursor-not-allowed' 
              : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {isVerifying ? 'Vérification...' : 'Se connecter'}
        </button>
      </form>
    </div>
  );
};

export default AuthForm; 